import { useState } from "react";
import { Form, Formik } from "formik";
import { number, object, string } from "yup";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import type { ProfileBailleur } from "./types";
import styles from "./ProfileAccountingSettings.module.css";

interface ProfileAccountingSettingsProps {
  profileBailleur: ProfileBailleur;
}

interface AccountingFormValues {
  customer_account_prefix: string;
  deposit_account_prefix: string;
  account_number_suffix_length: string;
}

const PREFIX_MESSAGE = "1 à 10 lettres ou chiffres, sans espace";

const prefixSchema = string()
  .required(PREFIX_MESSAGE)
  .matches(/^[A-Za-z0-9]{1,10}$/, PREFIX_MESSAGE);

const validationSchema = object({
  customer_account_prefix: prefixSchema,
  deposit_account_prefix: prefixSchema,
  account_number_suffix_length: number()
    .typeError("Entre 1 et 10 chiffres")
    .integer("Entre 1 et 10 chiffres")
    .min(1, "Entre 1 et 10 chiffres")
    .max(10, "Entre 1 et 10 chiffres")
    .required("Entre 1 et 10 chiffres"),
});

function exampleNumber(prefix: string, suffixLength: string): string {
  const width = parseInt(suffixLength) || 0;
  return `${prefix}${"1".padStart(width, "0")}`;
}

export default function ProfileAccountingSettings({
  profileBailleur,
}: ProfileAccountingSettingsProps) {
  const { updateProfileBailleur } = useProfileBailleurs();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <Card>
      <div className={styles.header}>
        <Title variant="hecto" weight="medium">
          Comptabilité du bailleur
        </Title>
        <Text color="muted">
          Chaque bail reçoit à sa création un compte client et un compte de
          dépôt de garantie dans votre export FEC, à la suite du dernier numéro
          utilisé. Les comptes déjà attribués ne changent pas.
        </Text>
      </div>
      <Formik<AccountingFormValues>
        enableReinitialize
        initialValues={{
          customer_account_prefix:
            profileBailleur.customer_account_prefix ?? "411",
          deposit_account_prefix:
            profileBailleur.deposit_account_prefix ?? "165",
          account_number_suffix_length: String(
            profileBailleur.account_number_suffix_length ?? 5,
          ),
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setError("");
          setSuccess("");
          try {
            await updateProfileBailleur(profileBailleur.id, {
              customer_account_prefix: values.customer_account_prefix,
              deposit_account_prefix: values.deposit_account_prefix,
              account_number_suffix_length: parseInt(
                values.account_number_suffix_length,
              ),
            });
            setSuccess("Numérotation enregistrée");
          } catch (e) {
            setError(handleAPIError(e));
          }
        }}
      >
        {({ values }) => (
          <Form className={styles.form}>
            <div className={styles.row}>
              <TextInput
                name="customer_account_prefix"
                label="Préfixe des comptes client"
                placeholder="411"
                marginForm=""
              />
              <TextInput
                name="deposit_account_prefix"
                label="Préfixe des comptes de dépôt"
                placeholder="165"
                marginForm=""
              />
              <TextInput
                name="account_number_suffix_length"
                label="Chiffres du suffixe"
                type="number"
                min={1}
                max={10}
                marginForm=""
              />
            </div>
            <Text variant="deci">
              Exemple de numérotation :{" "}
              <Text as="span" variant="deci" weight="bold">
                {exampleNumber(
                  values.customer_account_prefix,
                  values.account_number_suffix_length,
                )}
              </Text>{" "}
              pour le compte client,{" "}
              <Text as="span" variant="deci" weight="bold">
                {exampleNumber(
                  values.deposit_account_prefix,
                  values.account_number_suffix_length,
                )}
              </Text>{" "}
              pour le dépôt de garantie.
            </Text>
            <Callout variant="danger">{error}</Callout>
            <Callout variant="success">{success}</Callout>
            <FormikSubmitButton requiresWrite>Enregistrer</FormikSubmitButton>
          </Form>
        )}
      </Formik>
    </Card>
  );
}
